import { useQuery } from "@tanstack/react-query";
import { CircleAlert, ExternalLink, FileText, LoaderCircle, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { api, type CteList } from "@/client";
import { MetricStrip } from "@/components/MetricStrip";
import { QiveArchiveCoverage } from "@/components/QiveArchiveCoverage";
import { currentMonth } from "@/domain/time";
import { FinancePresentation } from "@/presentation/finance";

type Cte = CteList["documents"][number];

export function CtesView() {
	const month = currentMonth().id;
	const [search, setSearch] = useState("");
	const [role, setRole] = useState<"ALL" | "TAKER" | "NOT_TAKER">("ALL");
	const query = useQuery({
		queryKey: ["qive-archive", "ctes", month],
		queryFn: () => api.qiveArchive.ctes({ month }),
	});
	const documents = useMemo(() => {
		if (!query.data) {
			return [];
		}

		const term = search.trim().toLocaleLowerCase("pt-BR");

		return query.data.documents.filter(
			(document) =>
				(role === "ALL" || (role === "TAKER" ? document.isSindiTaker : !document.isSindiTaker)) &&
				(!term ||
					`${document.number} ${document.issuerName} ${document.takerName} ${document.senderName ?? ""} ${document.recipientName ?? ""} ${document.cargoDescription ?? ""}`
						.toLocaleLowerCase("pt-BR")
						.includes(term)),
		);
	}, [query.data, role, search]);

	if (query.isPending) {
		return (
			<div className="grid min-h-80 place-items-center text-sm font-medium text-stone-500">
				<span className="inline-flex items-center gap-2">
					<LoaderCircle className="size-4 animate-spin" />
					Abrindo os CT-es…
				</span>
			</div>
		);
	}

	if (query.error) {
		return (
			<div className="rounded-3xl border border-red-200 bg-white p-8 text-center">
				<CircleAlert className="mx-auto size-8 text-red-600" />
				<p className="mt-3 font-semibold text-stone-900">Não foi possível abrir os CT-es</p>
				<p className="mt-1 text-sm text-stone-500">{query.error.message}</p>
			</div>
		);
	}

	const data = query.data;

	return (
		<div className="space-y-6">
			<MetricStrip
				items={[
					{
						label: "CT-es no período",
						value: String(data.summary.count),
						detail: data.month.label,
					},
					{
						label: "Valor das prestações",
						value: FinancePresentation.money(data.summary.totalCents),
						detail: "não significa frete pago",
					},
					{
						label: "SINDI tomadora",
						value: String(data.summary.takerCount),
						detail: `${data.summary.notTakerCount} não tomadora`,
						tone: "success",
					},
					{
						label: "Cancelados",
						value: String(data.summary.canceledCount),
						detail: "eventos preservados",
						tone: data.summary.canceledCount > 0 ? "attention" : "neutral",
					},
				]}
			/>

			<QiveArchiveCoverage coverage={data.coverage} />

			<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="grid gap-3 border-b border-stone-200 p-5 lg:grid-cols-[minmax(260px,1fr)_220px]">
					<label className="relative">
						<Search className="pointer-events-none absolute left-3 top-3 size-4 text-stone-400" />
						<input
							type="search"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Buscar transportadora, parte, número ou carga"
							className="h-10 w-full rounded-xl border border-stone-200 pl-9 pr-3 text-sm outline-none focus:border-emerald-700"
						/>
					</label>
					<select
						value={role}
						onChange={(event) => {
							const value = event.target.value;
							setRole(value === "TAKER" || value === "NOT_TAKER" ? value : "ALL");
						}}
						className="h-10 rounded-xl border border-stone-200 bg-white px-3 text-sm outline-none focus:border-emerald-700"
					>
						<option value="ALL">Todos os papéis da SINDI</option>
						<option value="TAKER">SINDI tomadora</option>
						<option value="NOT_TAKER">SINDI não tomadora</option>
					</select>
				</header>

				<div className="divide-y divide-stone-100">
					{documents.map((document) => (
						<CteRow key={document.id} document={document} />
					))}
					{documents.length === 0 && (
						<p className="px-5 py-12 text-center text-sm text-stone-500">
							Nenhum CT-e encontrado com estes filtros.
						</p>
					)}
				</div>
			</section>
		</div>
	);
}

function CteRow({ document }: { document: Cte }) {
	const route =
		document.originCity && document.destinationCity
			? `${document.originCity}/${document.originState ?? ""} → ${document.destinationCity}/${document.destinationState ?? ""}`
			: "Rota não informada";
	const documentTypeLabel =
		document.documentType === "1"
			? "Complementar"
			: document.documentType === "2"
				? "Anulação"
				: document.documentType === "3"
					? "Substituição"
					: null;

	return (
		<article className="grid gap-4 px-5 py-5 xl:grid-cols-[120px_minmax(220px,1fr)_minmax(260px,1.2fr)_160px_auto] xl:items-center">
			<div>
				<p className="text-xs font-medium text-stone-500">
					{FinancePresentation.transactionDate(document.issuedOn)}
				</p>
				<p className="mt-1 text-sm font-semibold text-stone-950">CT-e {document.number}</p>
				{document.series && <p className="mt-1 text-xs text-stone-400">Série {document.series}</p>}
				<p
					className="mt-1 truncate font-mono text-[10px] text-stone-400"
					title={document.accessKey}
				>
					Chave {document.accessKey}
				</p>
			</div>
			<div className="min-w-0">
				<p className="truncate text-sm font-semibold text-stone-900">{document.issuerName}</p>
				<p className="mt-1 text-xs text-stone-500">{route}</p>
			</div>
			<div className="min-w-0">
				<p className="truncate text-sm text-stone-700">
					{document.cargoDescription ?? "Carga não descrita"}
				</p>
				<p className="mt-1 text-xs text-stone-500">
					{document.isSindiTaker ? "SINDI tomadora" : `Tomador: ${document.takerName}`}
					{document.referencedNfeKeys.length > 0 &&
						` · ${document.referencedNfeKeys.length} NF-e relacionada${document.referencedNfeKeys.length === 1 ? "" : "s"}`}
					{document.eventTypes.length > 0 &&
						` · ${document.eventTypes.length} evento${document.eventTypes.length === 1 ? "" : "s"}`}
				</p>
			</div>
			<div className="xl:text-right">
				<p className="text-sm font-semibold text-stone-950">
					{FinancePresentation.money(document.totalCents)}
				</p>
				<div className="mt-1 flex flex-wrap gap-1 xl:justify-end">
					<span
						className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
							document.isSindiTaker
								? "bg-emerald-100 text-emerald-800"
								: "bg-stone-100 text-stone-600"
						}`}
					>
						{document.isSindiTaker ? "Tomadora" : "Não tomadora"}
					</span>
					{document.status === "canceled" && (
						<span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-800">
							Cancelado
						</span>
					)}
					{documentTypeLabel && (
						<span className="rounded-full bg-sky-100 px-2 py-1 text-[11px] font-semibold text-sky-800">
							{documentTypeLabel}
						</span>
					)}
				</div>
			</div>
			<a
				href={`/api/cte-document.pdf?id=${encodeURIComponent(document.id)}`}
				target="_blank"
				rel="noreferrer"
				className="inline-flex h-9 items-center justify-center gap-2 rounded-xl border border-stone-200 px-3 text-xs font-semibold text-stone-700 transition hover:border-emerald-700 hover:text-emerald-800"
			>
				<FileText className="size-3.5" />
				DACTE
				<ExternalLink className="size-3" />
			</a>
		</article>
	);
}
